import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { updateUser } from '../../../redux/usersSlice';
import './UsernamePrompt.css';
import { useSelector } from 'react-redux';

const UsernamePrompt = ({ userId, onClose, token }) => {
  const [username, setUsername] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState(false);
  const dispatch = useDispatch();
  const projectInfo = useSelector((state) => state.projectInfo);

  // Validate username whenever it changes
  useEffect(() => {
    validateUsername(username);
  }, [username]);

  // Username validation function
  const validateUsername = (value) => {
    // Reset error message
    setError('');

    // Check if empty
    if (!value.trim()) {
      setIsValid(false);
      return;
    }

    // Check length (3-30 characters)
    if (value.length < 3 || value.length > 30) {
      setError('Username must be between 3-30 characters');
      setIsValid(false);
      return;
    }

    // Check for special characters (only allow letters, numbers, and underscores)
    const regex = /^[a-zA-Z0-9_]+$/;
    if (!regex.test(value)) {
      setError('Username can only contain letters, numbers, and underscores');
      setIsValid(false);
      return;
    }

    // If all checks pass
    setIsValid(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate again before submission
    validateUsername(username);
    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/user/update`,
        { first_name: username },
        {
          headers: {
            pubid: projectInfo?.pubid,
            channelid: projectInfo?.channelid,
            uid: projectInfo?.uid,
            "access-token": projectInfo?.accessToken,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        // Update the user in Redux store
        dispatch(updateUser({ id: userId, changes: { user_name: username } }));
        onClose();
      } else {
        setError('Failed to update username. Please try again.');
      }
    } catch (err) {
      console.error('Error updating username:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="username-prompt-overlay">
      <div className="username-prompt-container">
        <div className="username-prompt-header">
          <div className="welcome-icon">👋</div>
          <h2>Welcome to our Community!</h2>
        </div>

        <div className="username-prompt-content">
          <div className="prompt-info">
            <p>We notice you don't have a username yet.</p>
            <p className="prompt-highlight">Choose a unique username to personalize your experience and interact with other community members.</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label htmlFor="username">Your Username</label>              <div className="input-wrapper">
                <span className="input-icon">@</span>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username (3-30 characters)"
                  required
                  autoFocus
                  className={username && (isValid ? "valid-input" : "invalid-input")}
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <div className="input-requirements">
                Username must be 3-30 characters and contain only letters, numbers, and underscores.
              </div>
            </div>
            <div className="button-group">
              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting || !isValid}
              >
                {isSubmitting ? (
                  <><span className="spinner"></span>Saving...</>
                ) : (
                  <>Continue to Community</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UsernamePrompt;
